import type { UserSummaryV1 } from '@/model/User.model';
import { ELIGIBLE_MEMBER_STATUSES } from '@/model/UserGroups.model';
import { ResponseType } from '@/support/ApiService';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useCallback, useMemo } from 'react';

export interface EligibleMembers {
  users: Array<UserSummaryV1>;
  loading: boolean;
  loaded: boolean;
  failed: boolean;
  retry: () => void;
}

/**
 * Loads every user who may hold group membership in a single request.
 *
 * The group modals filter this list in the browser rather than querying per keystroke, which keeps
 * "Select all" unambiguous and lets ticks survive a search. `perPage: -1` is the internal API's
 * "no limit" convention.
 */
export const useEligibleMembers = (): EligibleMembers => {
  const { response, loading, invoke } = useApiHook({
    endpoint: 'GET /api/v1/users',
    query: { perPage: -1, status: [...ELIGIBLE_MEMBER_STATUSES] },
    options: { onError: { toast: false, capture: true, throw: false } },
  });

  const users = useMemo(() => (response?.type === ResponseType.Success ? response.body.data : []), [response]);

  const retry = useCallback(() => {
    invoke().catch(() => undefined);
  }, [invoke]);

  return {
    users,
    loading,
    loaded: response?.type === ResponseType.Success,
    failed: response?.type === ResponseType.Error,
    retry,
  };
};
